import { ButtonComponent } from '@/components/commons'
import { InputComponent } from '@/components/commons/forms/input/Input.component'
import { Text } from '@/components/commons/typography/Text'
import { Title } from '@/components/commons/typography/Title'
import { ArrowLeftIcon } from '@/public/assets/v2/images/icons'
import { useHighlightEditorStore } from '../store/highlightEditor'
import { HContentLink } from './highlight-editor/HContentLink'
import { HContentTypeSelector } from './highlight-editor/HContentTypeSelector'
import { Hstats } from './highlight-editor/HStats'
import { HAbout } from './highlight-editor/HAbout'
import { HContentCategories } from './highlight-editor/HContentCategories'
import {
  useCreateHighlight,
  useHighlightError,
  useUpdateHighlight,
} from '../hooks/useHighlights'
import { Highlight } from '../types/highlights'
import { Drawer } from '@/components/modal/Drawer'
import { cssClass } from '@/shared/utils'
import { helperTextStyle } from '@/components/pages/profile_v2/profileBar/styles/profilePersonalInfo.css'
import { Accordion } from '@mantine/core'
import { useProjectCreationStore } from 'containers/projects/store/project'
import clsx from 'clsx'
import { useContext } from 'react'
import { BrowserContext } from 'context'

interface HightlightFormModalProps {
  show: boolean
  useProjectId?: boolean
  onClose: () => void
  onSave?: (higlight?: Highlight) => void
}

const DEFAULT_HIGHLIGHT: Partial<Highlight> = {
  stats: [],
}

const MAX_CHAR_COUNT = 100

export const HightlightFormModal = ({
  show,
  useProjectId,
  onClose,
  onSave,
}: HightlightFormModalProps) => {
  const {
    highlight,
    updateHighlight,
    validateHighlight,
    setHighlight,
    setErrors,
  } = useHighlightEditorStore()
  const { mutateAsync: createHighlight, isLoading: creatingHighlight } =
    useCreateHighlight()
  const { mutateAsync: updateHighlightAsync, isLoading: updatingHighlight } =
    useUpdateHighlight()
  const { project } = useProjectCreationStore()
  const { errorMessage } = useHighlightError('title')

  const isLoading = creatingHighlight || updatingHighlight
  const { isMobile } = useContext(BrowserContext)

  const save = async () => {
    const isValid = validateHighlight(highlight)

    if (!isValid) return

    const highlightData = {
      ...DEFAULT_HIGHLIGHT,
      ...(highlight as Highlight),
    }

    if (useProjectId && project?.id) {
      const projectsInHighlight = highlightData.projects || []

      // Validate if project is already in the highlight
      if (!projectsInHighlight.includes(project?.id)) {
        projectsInHighlight.push(project?.id)
      }
    }

    let res

    if (highlight?.id) {
      res = await updateHighlightAsync(highlightData)
    } else {
      res = await createHighlight(highlightData)
    }

    onSave && onSave(res)
    close()
  }

  const close = () => {
    if (isLoading) return
    setHighlight(null)
    setErrors(null)
    onClose()
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedTitle = e.target.value
    const remainingCharacters = MAX_CHAR_COUNT - updatedTitle.length

    if (remainingCharacters >= 0) {
      updateHighlight({ title: updatedTitle })
    }
  }

  const remainingCharCount = MAX_CHAR_COUNT - (highlight?.title || '').length

  return (
    <Drawer
      className="--v2"
      position="right"
      opened={show}
      onClose={close}
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
      classNames={{
        content: 'bg-primary',
      }}
      style={{
        zIndex: 10000015,
      }}
      styles={{
        overlay: {
          zIndex: 10000014,
        },
        inner: {
          zIndex: 10000015,
        },
        content: {
          zIndex: 10000015,
        },
      }}
    >
      <div className={clsx(['px-10', isMobile && 'pt-60'])}>
        <div className="flex items-center justify-space-between sticky bg-primary top-0 left-0 right-0 z-20 pb-10 pt-16 border-b-solid border-b-1 border-secondary">
          <div
            className="flex items-center gap-6 cursor-pointer"
            onClick={onClose}
          >
            <ArrowLeftIcon className="text-tertirary" />
            <Text variation="small" colorClass="text-tertirary">
              Back
            </Text>
          </div>

          <div className="flex items-center gap-6 cursor-pointer">
            <ButtonComponent
              size="xSmall"
              variation="secondary"
              label="Cancel"
              onClick={close}
              loading={isLoading}
            />

            <ButtonComponent
              size="xSmall"
              variation="primary"
              label="Save"
              onClick={save}
              loading={isLoading}
            />
          </div>
        </div>

        <div className="flex flex-column pt-16">
          <div className="flex flex-column gap-8">
            <Title as="label" variation="xSmall">
              Title Your Highlight
              <Text as="sup" colorClass="text-info">
                *
              </Text>
            </Title>

            <InputComponent
              size="small"
              placeholder="Title"
              usePlaceholder
              className={['bg-white-primary']}
              value={highlight?.title}
              onChange={handleTitleChange}
              error={errorMessage}
            />
            <p
              className={cssClass([helperTextStyle, 'mb-12 ml-12 text-right'])}
            >
              {remainingCharCount} characters remaining
            </p>
          </div>

          <HAbout />
        </div>

        <div className="flex flex-column gap-32">
          <HContentLink />
          <HContentTypeSelector />

          <div className="gap-8">
            <Title as="label" variation="xSmall">
              Optional Fields
            </Title>

            <Accordion multiple>
              <Accordion.Item
                key="1"
                aria-label="Add Stats"
                value="Stats Showcase"
              >
                <Accordion.Control>
                  <Title
                    as="label"
                    variation="xSmall"
                    colorClass="text-secondary"
                  >
                    Stats Showcase
                  </Title>
                </Accordion.Control>
                <Accordion.Panel>
                  <Hstats />
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item
                key="2"
                aria-label="Add Tags"
                value="Content Tags"
              >
                <Accordion.Control>
                  <Title
                    as="label"
                    variation="xSmall"
                    colorClass="text-secondary"
                  >
                    Content Tags
                  </Title>
                </Accordion.Control>
                <Accordion.Panel>
                  <HContentCategories />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </div>

          <ButtonComponent
            loading={isLoading}
            variation="primary"
            label="Save"
            onClick={save}
            size="xSmall"
          />
        </div>
      </div>
    </Drawer>
  )
}
